import ActionTextIcon from "@/components/icons/ActionTextIcon";
import PopconfirmDeleteIcon from "@/components/icons/PopconfirmDeleteIcon";
import Ellipsis from "@/components/views/Ellipsis";
import { paginationTableProps, tableStandardProps } from "@/utils/componentUtils";
import { URLQuery } from "@/utils/queryUtils";
import { numberOrZero } from "@/utils/utils";
import { Card, Divider, Popconfirm, Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useDeleteConfigProperty, useFilterConfigProperties } from "../../../../common/api/queries";
import { TableSizes } from "../../../../common/constants";
import type { UUID } from "../../../../typings/global";
import { formatAgentName } from "../../../agent/utils";
import { DynamicConfigProperty, DynamicConfigPropertyFilterPageRequest } from "../../types";
import { CONFIG_PROPERTY_FEATURE_KEYS } from "../../utils";

interface Props {
  searchParams: DynamicConfigPropertyFilterPageRequest;
  onUpdateClick: (property: DynamicConfigProperty) => void;
}

export const ConfigPropertyTableView = ({ searchParams, onUpdateClick }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [visiblePropertyIds, setVisiblePropertyIds] = useState<UUID[]>([]);

  const { data, isFetching } = useFilterConfigProperties(searchParams);
  const deleteConfigProperty = useDeleteConfigProperty();

  useEffect(() => {
    setVisiblePropertyIds([]);
  }, [data]);

  const handlePropertyVisibilityToggle = (id: UUID) => {
    setVisiblePropertyIds((prevState) =>
      prevState.includes(id) ? prevState.filter((visibleId) => visibleId !== id) : [...prevState, id],
    );
  };

  const columns: ColumnsType<DynamicConfigProperty> = [
    {
      key: "key",
      title: t("configProperty.attrs.key"),
      width: 300,
      ellipsis: { showTitle: false },
      render: (_, record) => <Ellipsis>{record.key}</Ellipsis>,
    },
    {
      key: "value",
      align: "center",
      width: 50,
      render: (_, record) =>
        record.encrypted ? (
          <ActionTextIcon
            icon={visiblePropertyIds.includes(record.id) ? "eye-invisible" : "eye"}
            color={visiblePropertyIds.includes(record.id) ? "red" : "blue"}
            onClick={() => handlePropertyVisibilityToggle(record.id)}
          />
        ) : null,
    },
    {
      key: "value",
      title: t("configProperty.attrs.value"),
      width: 220,
      ellipsis: { showTitle: false },
      render: (_, record) => (
        <Ellipsis>
          {record.encrypted
            ? visiblePropertyIds.includes(record.id)
              ? record.value
              : "*************"
            : CONFIG_PROPERTY_FEATURE_KEYS.includes(record.key)
              ? record.value
                  ?.split(",")
                  .map((v) => t("agent.enums.feature." + v.trim()))
                  .join(", ")
              : record.value}
        </Ellipsis>
      ),
    },
    {
      key: "ownedBy",
      title: t("configProperty.attrs.ownedById"),
      width: 150,
      ellipsis: { showTitle: false },
      render: (_, record) => record.ownedBy && <Ellipsis>{formatAgentName(record.ownedBy, false)}</Ellipsis>,
    },
    {
      key: "actions",
      align: "right",
      fixed: "right",
      width: 180,
      render: (_, record) => (
        <>
          <ActionTextIcon icon="edit" color="blue" text={t("common.edit")} onClick={() => onUpdateClick(record)} />

          <Divider orientation="vertical" />

          <Popconfirm
            title={t("configProperty.helpers.deleteConfirm")}
            icon={<PopconfirmDeleteIcon />}
            cancelText={t("common.no")}
            okText={t("common.yes")}
            okType="danger"
            onConfirm={() => deleteConfigProperty.mutate(record.id)}
          >
            <ActionTextIcon icon="delete" color="red" text={t("common.delete")} />
          </Popconfirm>
        </>
      ),
    },
  ];

  return (
    <Card className="card-box">
      <Table<DynamicConfigProperty>
        {...tableStandardProps()}
        columns={columns}
        loading={isFetching}
        dataSource={data?.pageData}
        scroll={{ x: TableSizes.MEDIUM }}
        pagination={{
          ...paginationTableProps,
          current: numberOrZero(data?.pageIndex) + 1,
          pageSize: data?.pageSize,
          total: data?.totalElementsCount,
          onChange: (pageIndex) => navigate({ search: URLQuery.getTableChangePageIndex(pageIndex) }, { replace: true }),
        }}
      />
    </Card>
  );
};
